// src/api/category/category.ts
import axios from "axios";
import { API_BASE_PATH } from "../../environment";
import { toast } from "react-toastify";

const CATEGORY_BASE_URL = `${API_BASE_PATH}/enquiries/category`;

const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, ""); // strip numeric codes if any
  }

  return detail || details || message || "Something went wrong.";
};

// ✅ Interfaces
export interface CategoryPayload {
  cate_name: string;
}

export interface CategoryResponse {
  cate_id: number;
  cate_name: string;
  created_at: string;
  updated_at: string;
}

// ✅ Get all categories
export const getAllCategories = async (_token?: string | null) => {
  try {
    const response = await axios.get(`${CATEGORY_BASE_URL}/all`, {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + _token,
      },
    });
    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    console.error("Error fetching categories:", error);
    toast.error(message);
    return [];
  }
};

// ✅ Get category by ID
export const getCategoryById = async (
  cateId: number,
  _token?: string | null
) => {
  try {
    const response = await axios.get(`${CATEGORY_BASE_URL}/${cateId}`, {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + _token,
      },
    });
    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    console.error(`Error fetching category ${cateId}:`, error);
    toast.error(message);
    return null;
  }
};

// ✅ Create new category
export const createCategory = async (
  payload: CategoryPayload,
  _token?: string | null
): Promise<boolean> => {
  const formData = new URLSearchParams();
  formData.append("cate_name", payload.cate_name);

  try {
    const response = await axios.post(CATEGORY_BASE_URL, formData, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "application/json",
        Authorization: "Bearer " + _token,
      },
    });

    if (response.status === 200 || response.status === 201) {
      toast.success("Category created successfully");
      return true;
    } else {
      toast.error("Failed to create category");
      return false;
    }
  } catch (error: any) {
    if (error?.response?.data?.code === "DATABASE_ERROR") {
      toast.warning("Please provide a valid category name.");
      return false;
    }
    const message = extractErrorMessage(error);
    console.error("Error creating category:", error);
    toast.error(message);
    return false;
  }
};

// ✅ Update category
export const updateCategory = async (
  cateId: number,
  payload: CategoryPayload,
  _token?: string | null
): Promise<boolean> => {
  const formData = new URLSearchParams();
  formData.append("cate_name", payload.cate_name);

  try {
    const response = await axios.put(`${CATEGORY_BASE_URL}/${cateId}`, formData, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "application/json",
        Authorization: "Bearer " + _token,
      },
    });

    if (response.status === 200) {
      toast.success("Category updated successfully");
      return true;
    } else {
      toast.error("Failed to update category");
      return false;
    }
  } catch (error: any) {
    if (error?.response?.data?.code === "DATABASE_ERROR") {
      toast.warning("Please provide a valid category name.");
      return false;
    }
    const message = extractErrorMessage(error);
    console.error(`Error updating category ${cateId}:`, error);
    toast.error(message);
    return false;
  }
};


